<template>
  <v-card>
    <v-card-text>
      <div class="subtitle1">
        {{ $t("emailSettings.settingsTab.quarantineByAttachmentType") }}
      </div>
      <v-checkbox
        v-model="localValue.quarantineFileTypesEnabled"
        data-testid="quarantine-emails-checkbox"
        :ripple="false"
        :disabled="disabled"
      >
        <template #label>
          <div class="body2">
            {{ $t("emailSettings.settingsTab.quarantineEmails") }}
          </div>
        </template>
      </v-checkbox>
      <div class="subsettings ml-7 mt-2">
        <div class="mt-2">
          <template v-for="(_, index) of Math.ceil(fileTypesList.length / 6)">
            <v-row v-if="index <= 1 || showAll" :key="index" class="pl-4">
              <v-col
                v-for="fileType of fileTypesList.slice(index * 6, 6 + index * 6)"
                :key="fileType"
                cols="2"
                class="pa-0"
              >
                <v-checkbox
                  v-model="localValue.quarantineFileTypes"
                  :value="fileType"
                  :ripple="false"
                  :disabled="disabled"
                >
                  <template #label>
                    <div class="body2">{{ fileType }}</div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </template>
          <div class="d-flex align-center mb-4 mt-4" role="button" @click="showAll = !showAll">
            <span class="body2 coro-link">
              {{
                showAll
                  ? $t("emailSettings.settingsTab.seeLess")
                  : $t("emailSettings.settingsTab.seeAll")
              }}
            </span>
            <v-icon :class="{ rotated: showAll }" icon="$chevronDown" />
          </div>
          <v-combobox
            v-model="localValue.quarantineCustomFileTypes"
            clearable
            clear-icon="$x"
            :label="$t('emailSettings.settingsTab.customFileTypes')"
            :aria-label="$t('emailSettings.settingsTab.customFileTypes')"
            data-testid="file-types-combobox"
            variant="outlined"
            multiple
            :disabled="disabled || !localValue.quarantineFileTypesEnabled"
            :placeholder="$t('emailSettings.settingsTab.customFileTypes')"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                :disabled="disabled || !localValue.quarantineFileTypesEnabled"
                variant="flat"
                size="default"
                color="indigo-faint"
                close-icon="$closeCircle"
                :text="item.title"
              >
                <div class="d-flex align-center">
                  <span class="ml-1 mr-2">{{ item.title }}</span>
                </div>
              </v-chip>
            </template>
            <template #item="data">
              <v-list-item>
                <span class="ml-1">{{ data.item }}</span>
              </v-list-item>
            </template>
          </v-combobox>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType, toRefs } from "vue";
import type { EmailSecuritySettings } from "@/_store/email-security/email-settings.module.ts";
import { AttachmentFileExtension } from "@/constants/emails.ts";

import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  name: "QuarantineFileTypesSettings",
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const localValue = useTwoWayBinding(modelValue, (value) => {
      emit("update:modelValue", value);
    });

    const showAll = ref(false);
    const fileTypesList = [
      AttachmentFileExtension.SEVEN_ZIP,
      AttachmentFileExtension.BAT,
      AttachmentFileExtension.BIN,
      AttachmentFileExtension.CHM,
      AttachmentFileExtension.CMD,
      AttachmentFileExtension.COM,
      AttachmentFileExtension.CPL,
      AttachmentFileExtension.DLL,
      AttachmentFileExtension.DMG,
      AttachmentFileExtension.EXE,
      AttachmentFileExtension.GADGET,
      AttachmentFileExtension.G7,
      AttachmentFileExtension.GZ,
      AttachmentFileExtension.HTA,
      AttachmentFileExtension.HTM,
      AttachmentFileExtension.HTML,
      AttachmentFileExtension.INF,
      AttachmentFileExtension.ISO,
      AttachmentFileExtension.JAR,
      AttachmentFileExtension.JS,
      AttachmentFileExtension.JSE,
      AttachmentFileExtension.INK,
      AttachmentFileExtension.LNK,
      AttachmentFileExtension.MHT,
      AttachmentFileExtension.MSC,
      AttachmentFileExtension.MSH,
      AttachmentFileExtension.MSH1,
      AttachmentFileExtension.MSH1XML,
      AttachmentFileExtension.MSH2,
      AttachmentFileExtension.MSH2XML,
      AttachmentFileExtension.MSHL,
      AttachmentFileExtension.MSHXML,
      AttachmentFileExtension.MSI,
      AttachmentFileExtension.MSP,
      AttachmentFileExtension.PIF,
      AttachmentFileExtension.PS,
      AttachmentFileExtension.PS1,
      AttachmentFileExtension.PS1XML,
      AttachmentFileExtension.PS2,
      AttachmentFileExtension.PS2XML,
      AttachmentFileExtension.PSC1,
      AttachmentFileExtension.PSC2,
      AttachmentFileExtension.PSCL,
      AttachmentFileExtension.PSL,
      AttachmentFileExtension.PY,
      AttachmentFileExtension.RAR,
      AttachmentFileExtension.REG,
      AttachmentFileExtension.SCF,
      AttachmentFileExtension.SCR,
      AttachmentFileExtension.SH,
      AttachmentFileExtension.TAR,
      AttachmentFileExtension.VB,
      AttachmentFileExtension.VBE,
      AttachmentFileExtension.VBS,
      AttachmentFileExtension.ZIP,
      AttachmentFileExtension.WAS,
      AttachmentFileExtension.WS,
      AttachmentFileExtension.WSC,
      AttachmentFileExtension.WSF,
      AttachmentFileExtension.WSH,
    ];

    return {
      localValue,
      showAll,
      fileTypesList,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .icon-chevron-down-xs:before {
    color: rgb(var(--v-theme-link)) !important;
  }

  .v-field {
    .v-chip {
      --v-chip-height: 32px;
    }

    .v-field__input {
      row-gap: 12px;
    }
  }

  label {
    opacity: 1;
  }
}
</style>
